import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  CircularProgress,
  MenuItem,
  LinearProgress,
} from "@mui/material";
import { useUpdatePartInstructionMutation } from "@/store/api/adminApi.js";
import { SECTION } from "@/constants/examConstants";
import FileUploadButton from "@/components/common/FileUploadButton";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const PartEditDialog = ({ dialogOpen, handleCloseDialog, part }) => {
  const [updatePart, { isLoading: isUpdating, error: partError }] =
    useUpdatePartInstructionMutation();
  const { uploadProgress } = useSelector((state) => state.upload);

  // Form State
  const [formData, setFormData] = useState({
    partNumber: "",
    title: "",
    section: SECTION.LISTENING,
    instructions: "",
    imageUrl: "",
    audioUrl: "",
    imagePreviewUrl: "",
    audioPreviewUrl: "",
  });

  useEffect(() => {
    if (part) {
      setFormData({
        partNumber: part.partNumber || "",
        title: part.title || "",
        section: part.section || SECTION.LISTENING,
        instructions: part.instructions || "",
        imageUrl: part.imageUrl || "",
        audioUrl: part.audioUrl || "",
        imagePreviewUrl: part.imageUrl || "",
        audioPreviewUrl: part.audioUrl || "",
      });
    }
  }, [part]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const { partNumber, title, section, instructions, image, audio } = formData;
    // migrate to form data
    const partData = new FormData();
    partData.append("partNumber", partNumber);
    partData.append("title", title);
    partData.append("section", section);
    partData.append("instructions", instructions);
    partData.append("image", image);
    partData.append("audio", audio);
    try {
      await updatePart({ id: part._id, instructionData: partData }).unwrap();
      toast.success("Part updated successfully");
      onClose();
    } catch (err) {
      console.error("Failed to save part:", err);
      toast.error("Failed to update part: " + err?.message);
    }
  };
  const handleFileChange = (obj) => {
    const { name, file } = obj || {};
    if (file && name) {
      setFormData((prev) => ({
        ...prev,
        [name]: file,
        [`${name}PreviewUrl`]: URL.createObjectURL(file),
      }));
    }
  };

  const resetPart = () => {
    setFormData({
      partNumber: "",
      title: "",
      section: SECTION.LISTENING,
      instructions: "",
      image: null,
      audio: null,
      imagePreviewUrl: "",
      audioPreviewUrl: "",
    });
  };

  const onClose = () => {
    resetPart();
    handleCloseDialog();
  };

  if (!part) return null;

  return (
    <Dialog
      open={dialogOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit Part</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Part Number"
              type="number"
              value={formData.partNumber}
              onChange={(e) => handleFormChange("partNumber", e.target.value)}
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              fullWidth
              label="Part Title"
              value={formData.title}
              onChange={(e) => handleFormChange("title", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Section"
              value={formData.section}
              onChange={(e) => handleFormChange("section", e.target.value)}
            >
              <MenuItem value={SECTION.LISTENING}>Listening</MenuItem>
              <MenuItem value={SECTION.READING}>Reading</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Instructions"
              value={formData.instructions}
              onChange={(e) => handleFormChange("instructions", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            {/* <TextField
              fullWidth
              label="Image URL"
              value={formData.imageUrl}
              onChange={(e) => handleFormChange("imageUrl", e.target.value)}
            /> */}
            <FileUploadButton
              name="image"
              label="Upload Image"
              accept="image/*"
              onFileSelect={handleFileChange}
              file={formData.image}
              previewUrl={formData.imagePreviewUrl}
              onClear={() => {
                setFormData((prev) => ({
                  ...prev,
                  image: null,
                  imagePreviewUrl: "",
                }));
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FileUploadButton
              name="audio"
              label="Upload Audio"
              accept="audio/*"
              onFileSelect={handleFileChange}
              file={formData.audio}
              previewUrl={formData.audioPreviewUrl}
              onClear={() => {
                setFormData((prev) => ({
                  ...prev,
                  audio: null,
                  audioPreviewUrl: "",
                }));
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isUpdating}
        >
          {isUpdating ? <CircularProgress size={24} /> : "Save"}
        </Button>
      </DialogActions>
      {isUpdating && (
        <LinearProgress
          variant={uploadProgress > 0 ? "determinate" : "indeterminate"}
          value={uploadProgress}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          }}
        />
      )}
    </Dialog>
  );
};

export default PartEditDialog;
